import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Alert, AlertDescription } from './ui/alert';
import PasswordStrengthIndicator from './PasswordStrengthIndicator';
import { Loader2, Lock, Eye, EyeOff, AlertCircle } from 'lucide-react';

const CambioPasswordObligatorio = ({ onSuccess }) => {
  const { api, logout } = useAuth();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [formData, setFormData] = useState({
    password_nueva: '',
    password_confirmar: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password_nueva !== formData.password_confirmar) {
      setError('Las contraseñas no coinciden');
      return;
    }

    setSaving(true);
    try {
      await api.post('/auth/cambiar-password-obligatorio', formData);
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al cambiar la contraseña');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    logout();
  };

  return (
    <Dialog open={true} onOpenChange={() => {}}>
      <DialogContent
        className="max-w-md [&>button]:hidden"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" data-testid="cambio-password-title">
            <Lock className="w-5 h-5" />
            Cambio de contraseña requerido
          </DialogTitle>
        </DialogHeader>

        <p className="text-sm text-slate-500">
          Su administrador requiere que cambie su contraseña antes de continuar.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-2">
            <Label htmlFor="password_nueva">Nueva contraseña *</Label>
            <div className="relative">
              <Input
                id="password_nueva"
                type={showPassword ? 'text' : 'password'}
                data-testid="nueva-password-input"
                value={formData.password_nueva}
                onChange={(e) => setFormData({ ...formData, password_nueva: e.target.value })}
                placeholder="Ingrese su nueva contraseña"
                required
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="absolute right-0 top-0"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </Button>
            </div>
            <PasswordStrengthIndicator password={formData.password_nueva} />
          </div>

          <div className="space-y-2">
            <Label htmlFor="password_confirmar">Confirmar contraseña *</Label>
            <div className="relative">
              <Input
                id="password_confirmar"
                type={showConfirm ? 'text' : 'password'}
                data-testid="confirmar-password-input"
                value={formData.password_confirmar}
                onChange={(e) => setFormData({ ...formData, password_confirmar: e.target.value })}
                placeholder="Repita su nueva contraseña"
                required
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="absolute right-0 top-0"
                onClick={() => setShowConfirm(!showConfirm)}
              >
                {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </Button>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              data-testid="cancelar-cambio-password-btn"
            >
              Cancelar (cerrar sesión)
            </Button>
            <Button
              type="submit"
              disabled={saving}
              data-testid="guardar-cambio-password-btn"
              className="bg-slate-900 text-white hover:bg-slate-800"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Cambiar contraseña
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CambioPasswordObligatorio;
